import { Box } from '@mui/material';
import { AppImages } from 'app/appImages';
import { MprcSimpleDialog } from 'components/MprcSimpleDialog';
import { MprcVideoBanner } from 'components/MprcVideoBanner';
import { TrainingMeetingItemDto } from 'generated/CoreApi_v1';
import { useCoreApi } from 'providers/CoreApiProvider';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { OnProgressProps } from 'react-player/base';
import { NIL } from 'uuid';

/////////////////////////////////////////////////////////////////////////////////////

const TrackingLowerThresholdSeconds = 5;
const TrackingHighThresholdSeconds = 7;

interface IProps {
    enrollmentId: string;
    meeting: TrainingMeetingItemDto;
    onClose: () => void;
}

/////////////////////////////////////////////////////////////////////////////////////

export const MeetingRecordingViewer: React.FC<IProps> = ({ enrollmentId, meeting, onClose }) => {

    const api = useCoreApi();
    const { t } = useTranslation("lms");

    const file = meeting.recordingFile;

    const firstPlay = useRef<boolean>(true);
    const lastMediaPositionSaved = useRef<number>(meeting.enrollmentData?.recordingFullPlayed === true ? 0 : meeting.enrollmentData?.recordingLastPosition ?? 0);
    const currentMediaPosition = useRef<number>(0);

    ///
    const onConfirmMediaSeek = (id: string, seekSeconds: number) => {
        return { playTime: meeting.enrollmentData?.canSeekRecording === true ? seekSeconds : Math.min(seekSeconds, currentMediaPosition.current ?? 0), threshold: 0 };
    };

    ///
    const onMediaPlay = (id: string, seconds: number) => {

        if (meeting.enrollmentData?.recordingLastPosition == null) {
            // Não precisa verificar SEEK.
            return null;
        }

        if (!firstPlay.current) {
            // Não é a primeira vez que pressionou PLAY, então verificar se pode fazer SEEK.
            const { playTime } = onConfirmMediaSeek("", seconds);
            return playTime;
        }

        // Ao pressionar PLAY a primeira vez, posiciona a mídia na última posição registrada.
        firstPlay.current = false;
        return lastMediaPositionSaved.current;
    };

    ///
    const doTrackingView = (playedSeconds: number, endOfMedia: boolean) => {
        if (enrollmentId === NIL) return;
        api.v1.enrollment.meetingRecordingTracking({ enrollmentId, componentId: meeting.componentId, playTime: playedSeconds, fullPlayed: endOfMedia })
            .then(({ data: success }) => {
                if (success) lastMediaPositionSaved.current = playedSeconds;
            });
    };

    ///
    const trackingView = (playedSeconds: number, endOfMedia: boolean) => {

        const lp = currentMediaPosition.current;
        if (playedSeconds > lp || endOfMedia) {
            // Grava a cada n segundos ou se terminou.
            const diffTime = Math.trunc(playedSeconds - lp);
            // Tolerância de 2s caso não possa fazer SEEK.
            const maxDiffTime = meeting.enrollmentData?.canSeekRecording === false ? TrackingHighThresholdSeconds : Number.MAX_SAFE_INTEGER;

            if (endOfMedia
                || (diffTime >= TrackingLowerThresholdSeconds && meeting.enrollmentData?.canSeekRecording === false)
                || (diffTime >= TrackingLowerThresholdSeconds && diffTime < maxDiffTime)
            ) {
                // Salva última posição (localmente).
                currentMediaPosition.current = playedSeconds;

                // Salva o tracking assíncronamente.
                doTrackingView(playedSeconds, endOfMedia);
            }
        }
    };

    ///
    const trackingProgress = (id: string, state: OnProgressProps) => {
        if (state.playedSeconds > 0 && meeting.enrollmentData?.recordingFullPlayed !== true) {
            trackingView(state.playedSeconds, false);
        }
    };

    if (!file) return null;

    return (

        <MprcSimpleDialog
            title={t("Student.EnrollmentView.Content.Meeting.RecordingViewerTitle")}
            subtitle={meeting.name}
            width="xl"
            message={
                <Box sx={{ width: "100%", height: "80vh" }}>
                    <MprcVideoBanner
                        id={file.fileId}
                        videoId={file.fileId}
                        type="custom-video-slide"
                        url={file.url!}
                        description={file.metadata.title}
                        height={file.metadata.height!}
                        width={file.metadata.width!}
                        duration={file.metadata.duration ?? 0}
                        mimetype={file.mimetype}
                        isAudio={file.mimetype.indexOf("audio") > -1}
                        isHls={false}
                        autoPlay={true}
                        poster={AppImages.VideoMp4Poster}
                        offset={0}
                        stopPlayback={false}
                        onProgress={trackingProgress}
                        onEnded={(_, playedSeconds) => trackingView(playedSeconds, true)}
                        onConfirmSeek={onConfirmMediaSeek}
                        onPlay={onMediaPlay}
                    />
                </Box>
            }
            onClose={onClose}
        />
    );
};
